import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const navbarBg = isHomePage 
    ? scrolled 
      ? 'bg-white/90 shadow-md' 
      : 'bg-transparent'
    : 'bg-white/90 shadow-md';

  const textColor = isHomePage && !scrolled ? 'text-white' : 'text-dk-gray';

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-300 backdrop-blur-sm ${navbarBg}`}
      style={{ 
        transform: 'translate3d(0,0,0)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        willChange: 'transform',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        WebkitTransform: 'translate3d(0,0,0)',
        WebkitPerspective: '1000',
        WebkitTransformStyle: 'preserve-3d'
      }}
    >
      <div className="container mx-auto py-4">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            <img
              src="https://photoeasyweb.eu/wp-content/uploads/2025/10/Pied-de-page-Haut-de-page-1.png"
              alt="DIAGNOSTI-K Logo"
              className={`h-24 md:h-40 w-auto object-contain transition-all duration-300 ${scrolled ? 'md:-my-10' : 'md:-my-10'} -my-8`}
            />
          </Link>

          {/* Desktop Menu */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`font-medium hover:text-dk-blue transition-colors ${
                location.pathname === '/' ? (isHomePage && !scrolled ? 'text-white' : 'text-dk-blue') : textColor
              }`}
            >
              Accueil
            </Link>
            <Link
              to="/services"
              className={`font-medium hover:text-dk-blue transition-colors ${
                location.pathname === '/services' ? (isHomePage && !scrolled ? 'text-white' : 'text-dk-blue') : textColor
              }`}
            >
              Services
            </Link>
            <Link
              to="/audit-energetique"
              className={`font-medium hover:text-dk-blue transition-colors ${
                location.pathname === '/audit-energetique' ? (isHomePage && !scrolled ? 'text-white' : 'text-dk-blue') : textColor
              }`}
            >
              Audit Énergétique
            </Link>
            <Link
              to="/realisations"
              className={`font-medium hover:text-dk-blue transition-colors ${
                location.pathname === '/realisations' ? (isHomePage && !scrolled ? 'text-white' : 'text-dk-blue') : textColor
              }`}
            >
              Réalisations
            </Link>
            <Link
              to="/contact"
              className={`font-medium hover:text-dk-blue transition-colors ${
                location.pathname === '/contact' ? (isHomePage && !scrolled ? 'text-white' : 'text-dk-blue') : textColor
              }`}
            >
              Contact
            </Link>
            <a
              href="tel:0789307500"
              className={`btn ${isHomePage && !scrolled ? 'bg-white text-dk-blue hover:bg-gray-100' : 'btn-primary'} flex items-center`}
            >
              <Phone className="h-4 w-4 mr-2" />
              Prendre RDV
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 ${textColor}`}
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-white z-50 md:hidden"
          style={{
            top: '72px',
            height: 'calc(100vh - 72px)',
            overflow: 'hidden'
          }}
        >
          <div className="container h-full py-6">
            <div className="flex flex-col space-y-6">
              <Link
                to="/"
                className={`font-medium py-2 hover:text-dk-blue transition-colors ${
                  location.pathname === '/' ? 'text-dk-blue' : 'text-dk-gray'
                }`}
              >
                Accueil
              </Link>
              <Link
                to="/services"
                className={`font-medium py-2 hover:text-dk-blue transition-colors ${
                  location.pathname === '/services' ? 'text-dk-blue' : 'text-dk-gray'
                }`}
              >
                Services
              </Link>
              <Link
                to="/audit-energetique"
                className={`font-medium py-2 hover:text-dk-blue transition-colors ${
                  location.pathname === '/audit-energetique' ? 'text-dk-blue' : 'text-dk-gray'
                }`}
              >
                Audit Énergétique
              </Link>
              <Link
                to="/realisations"
                className={`font-medium py-2 hover:text-dk-blue transition-colors ${
                  location.pathname === '/realisations' ? 'text-dk-blue' : 'text-dk-gray'
                }`}
              >
                Réalisations
              </Link>
              <Link
                to="/contact"
                className={`font-medium py-2 hover:text-dk-blue transition-colors ${
                  location.pathname === '/contact' ? 'text-dk-blue' : 'text-dk-gray'
                }`}
              >
                Contact
              </Link>
              <a
                href="tel:0789307500"
                className="btn-primary flex items-center justify-center"
              >
                <Phone className="h-4 w-4 mr-2" />
                Prendre RDV
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </header>
  );
};

export default Navbar;